import {
  useMutation as useOriginalMutation,
  type OperationVariables,
} from '@apollo/client'

import { removeDirectivesFromDocument } from '@apollo/client/utilities'
import { DocumentNode } from 'graphql'

import { useFixedMutationTuple } from './useFixedMutationTuple'
import { useErrorRedirect } from './useErrorRedirect'
import { useParseErrorHandler } from './useParseErrorHandler'
import {
  MutationHookOptions,
  MutationOptionsWithRedirectedErrors,
  MutationTuple,
} from './useMutation.types'

export function useMutation<
  Data extends Record<string, any>,
  Variables extends OperationVariables = OperationVariables,
>(
  _mutation: DocumentNode,
  options: MutationHookOptions<Data, Variables>
): MutationTuple<Data, Variables> {
  const { onError, ignoreResults } = options || {}
  const { UNSAFE_ignoreResults } = (options ||
    {}) as MutationOptionsWithRedirectedErrors<Data, Variables>

  const { onUserErrorHandler, onApolloErrorHandler, onErrorDispatch } =
    useParseErrorHandler({ onError })

  const augmentedOptions = useErrorRedirect<Data, Variables>(options, {
    onUserErrorHandler,
  })

  const ignoreResultsFinal = ignoreResults || UNSAFE_ignoreResults

  // TODO remove @client directives, see: https://sketch.slack.com/archives/C09BF8KAXB8/p1756819151670889
  const mutation =
    process.env.NODE_ENV === 'test' &&
    process.env.REMOVE_CLIENT_DIRECTIVE === 'true'
      ? removeDirectivesFromDocument([{ name: 'client' }], _mutation) ||
        _mutation
      : _mutation

  const originalMutationTuple = useOriginalMutation<Data, Variables>(mutation, {
    ...(ignoreResultsFinal ? { ignoreResults: ignoreResultsFinal } : {}),
    ...options,
    // here we expect to optionally override `onCompleted`, `update` and `refetchQueries`
    // so we have to make sure that `augmentedOptions` are passed after general all `options`
    ...augmentedOptions,
    onError: onApolloErrorHandler,
    // TODO: Fix useMutation options TypeScript typing, see https://linear.app/sketch/issue/WEB-2252
  } as any)

  const mutationTuple = useFixedMutationTuple({
    onApolloErrorHandler,
    onErrorDispatch,
    originalMutationTuple,
    redirectErrors: options.redirectErrors,
  })

  return mutationTuple
}
